"use client";

// OsNavigator — the morphing header that sits above every level: the active
// node as a large title with its siblings trailing as a faded, slideable nav;
// an inline-editable description; a stat line (count · last-active, with a live
// organism dot); and the view-chips that change per level. In "compact" mode
// (a document is open) it shrinks to give the reading surface room.
//
// The sibling strip is a lived-in carousel: it scrolls horizontally (with no
// visible scrollbar and iOS momentum), drags to pan with the pointer on desktop
// (click-and-drag, with a little inertia), and quietly follows the active
// sibling into view. Hover is the global tracker's (components/HoverField.tsx,
// law_nav_proximity): as the pointer comes near the rail every word lifts to
// ~.5 and the one under it to ~.8, all in CSS (`.os-sib-word`); a finger still
// gets the soft spring press. The leftmost item always reaches fully to the
// left edge — no clipping.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { relTime, type OsNode } from "../../lib/os/tree";
import EditableText from "./EditableText";
import ReorderableChips from "./ReorderableChips";
import ChipsHandoff from "./ChipsHandoff";
import { mayStartRailDrag } from "./railDrag";
import { useFlip } from "../../lib/hooks/useFlip";
import { useNaturalHeight } from "../../lib/os/useNaturalHeight";
import { motion, useHoverFeel } from "../../lib/motion";

const useIso = typeof window !== "undefined" ? useLayoutEffect : useEffect;

/** how long a pressed word stands without its place arriving before it lets go */
const PRESS_HOLD_MS = 900;

export interface ChipDef {
  id: string;
  label: string;
}

export default function OsNavigator({
  siblings,
  activeKey,
  onSelectSibling,
  summary,
  summaryManual,
  showMatter = true,
  onEditSummary,
  chips,
  view,
  onView,
  onNewView,
  onReorderChips,
  allowNewView = true,
  compact = false,
  navOffset = 0,
  renderChipAccessory,
  unseenViews,
  chipTrailing,
  onChipContextMenu,
  fold = false,
  foldRail = false,
  quiet = false,
  chipSlot,
  pressAhead = false,
}: {
  siblings: OsNode[];
  activeKey: string;
  onSelectSibling: (key: string) => void;
  summary: string;
  summaryManual: boolean;
  /** law_say_it_once — the matter line (the description under the head) is shown
   *  only when it was written by a person: a manual summary or a project's real
   *  tagline. A generated one restates what the head and the stat line already
   *  say, so the caller passes false and the string rides in a hover hint on the
   *  stat line instead. Default true: callers whose description is hand-authored
   *  (admin, docs, rooms) are unchanged. */
  showMatter?: boolean;
  onEditSummary: (text: string) => void;
  chips: ChipDef[];
  view: string;
  onView: (id: string) => void;
  onNewView: () => void;
  onReorderChips: (ids: string[]) => void;
  /** show the "+ new view" chip (false for fixed view-sets like /admin). */
  allowNewView?: boolean;
  compact?: boolean;
  navOffset?: number;
  /** optional trailing element inside a chip (the orbit dial). */
  renderChipAccessory?: (id: string) => React.ReactNode;
  /** view ids not yet opened (onboarding reveal) — they shimmer until first tap. */
  unseenViews?: Set<string>;
  /** seated at the END of the chip row, pushed right — where a view that owns
   *  sub-views hangs them, instead of opening a second row of chips beneath
   *  (law_one_chip_row). */
  chipTrailing?: React.ReactNode;
  /** right-click a chip (a made view can be let go of). */
  onChipContextMenu?: (id: string) => void;
  /** law_scroll_lock — when the navigator is PINNED chrome, the description and
   *  the stat line fold away as the level's content scrolls under it, leaving the
   *  title strip and the view chips. Off inside the documents reader, where the
   *  whole navigator already folds as one block. */
  fold?: boolean;
  /** THE READER'S FOLD (law_stage, 2026-10-12): the title strip is what folds
   *  away as you read, and the view chips stay — they rise into its place, so
   *  the views are one press away mid-document. Used with `quiet` (a reader's
   *  navigator carries no matter of its own). */
  foldRail?: boolean;
  /** THE PANEL'S CHIPS (law_stage): on a level with no chips of its own (a
   *  panel), a slot where the chips stand, for the panel to hand its own view
   *  chips into (components/os/NavChips). It follows the fold like the chips do. */
  chipSlot?: (el: HTMLDivElement | null) => void;
  /** during the onboarding walk the descriptive half (summary + stat line) rests
   *  entirely — netti carries the words, so the page holds only the title strip
   *  and its views and the focus stays on the tour. */
  quiet?: boolean;
  /** law_snappy: the word pressed answers on this frame, before its place arrives.
   *  For a caller whose move is a transition (OsShell's goTo); elsewhere the
   *  ticker moves with the place, as it always has. */
  pressAhead?: boolean;
}) {
  const active = siblings.find((s) => s.key === activeKey) ?? siblings[0];
  const activeIdx = Math.max(0, siblings.findIndex((s) => s.key === activeKey));
  // law_snappy — the word you press answers on this frame. The move itself is a
  // transition (OsShell's goTo), so the place, its line and its chips arrive a
  // frame later, together; until they do the ticker stands on the word pressed.
  // A move that never lands lets it go again.
  const [pressed, setPressed] = useState<string | null>(null);
  useEffect(() => { setPressed(null); }, [activeKey]);
  useEffect(() => {
    if (!pressed) return;
    const t = window.setTimeout(() => setPressed(null), PRESS_HOLD_MS);
    return () => window.clearTimeout(t);
  }, [pressed]);
  const pressedIdx = pressed ? siblings.findIndex((s) => s.key === pressed) : -1;
  const tickIdx = pressedIdx >= 0 ? pressedIdx : activeIdx;
  const go = useCallback(
    (key: string) => {
      if (pressAhead && key !== activeKey) setPressed(key);
      onSelectSibling(key);
    },
    [pressAhead, activeKey, onSelectSibling],
  );
  // which way the last step between siblings went (+1 to the right, −1 to the
  // left; a wrap keeps its visual direction), and whether THIS render is that
  // step. The chips hand over on it (ChipsHandoff), from the side the page does.
  const lastPlace = useRef(activeKey);
  const placeDir = useRef<1 | -1>(1);
  const placeChanged = lastPlace.current !== activeKey;
  if (placeChanged) {
    const from = siblings.findIndex((s) => s.key === lastPlace.current);
    if (from >= 0) {
      const d = activeIdx - from;
      placeDir.current = siblings.length > 2 && Math.abs(d) === siblings.length - 1 ? (d > 0 ? -1 : 1) : d < 0 ? -1 : 1;
    }
  }
  useEffect(() => { lastPlace.current = activeKey; }, [activeKey]);

  // The strip scrolls natively (hidden scrollbar) and is pointer-draggable on
  // desktop via useHDragScroll. We auto-follow the active sibling into view with
  // scrollTo — same mechanism as the user's own scroll/drag, so they never fight.
  const railRef = useRef<HTMLDivElement>(null);

  // TICKER (the Figma "Slidebar"): the SELECTED sibling slides to the FAR LEFT in
  // full ink; the next siblings trail to the right, faded; items that pass the left
  // edge blur + fade out, and a second copy rides behind so they reappear from the
  // right — an infinite-feeling ticker. Drag/swipe left-right (mobile AND desktop)
  // switches section; tapping a sibling selects it. The whole strip translates on a
  // soft CSS glide so the active word eases to the left rather than jumping.
  const n = siblings.length;
  const [offsets, setOffsets] = useState<number[]>([]);
  // the live drag offset is NOT state: it is written straight onto the strip as
  // --drag-dx on each pointermove (see setDrag). Holding it in state rendered
  // the whole navigator — every word, every chip — once per pointer event, and
  // on a fast mouse that was hundreds of renders a second: the stutter, and on
  // 2026-09-26 the render storm React stopped with error #185 (the top frame
  // was this very setter), leaving the rail stuck mid-drag and every click
  // after it ignored.
  const dragDxRef = useRef(0);
  const [dragging, setDragging] = useState(false);
  // when the sibling SET changes (spaces unlocking at the end of onboarding, say),
  // the active word lands at a NEW index/offset in the same render. Snap the strip
  // to it for one frame so it doesn't visibly glide in from the wrong spot (offset 0)
  // under the 0.32s transform transition.
  const [snapSet, setSnapSet] = useState(false);

  // A space that UNLOCKS mid-session (the onboarding odyssey) announces itself:
  // the newly-arrived word shimmers for a moment (law_sequential_unfold). Keys
  // present on first mount never shimmer — only genuine arrivals.
  const seenKeys = useRef<Set<string> | null>(null);
  const [freshKeys, setFreshKeys] = useState<Set<string>>(new Set());
  // `key` is the word the press began on: a press that wobbled a few px on its
  // way up is still a click on that word (see onRailUp).
  const dragState = useRef<{ x0: number; moved: boolean; pid: number; key: string | null } | null>(null);
  const didDragRef = useRef(false);
  const keysSig = siblings.map((s) => s.key).join("|");
  // THE TINTED WORD. This effect used to depend on the `siblings` ARRAY, which
  // is rebuilt on every shell render — so the re-run's cleanup cancelled the
  // 2.2s timer that takes the shimmer off, and the next run (no new keys)
  // never armed another. The word kept `nav-word-shimmer` for good, frozen on
  // the animation's last frame: a half-accent tint that read as a broken tab
  // ("connections" lit orange while its siblings sat in ink). Keyed on the
  // SIGNATURE now, and the timer is held in a ref so it survives re-renders.
  const shimmerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const keys = keysSig ? keysSig.split("|") : [];
    if (seenKeys.current === null) { seenKeys.current = new Set(keys); return; }
    const fresh = keys.filter((k) => !seenKeys.current!.has(k));
    if (!fresh.length) return;
    fresh.forEach((k) => seenKeys.current!.add(k));
    setFreshKeys(new Set(fresh));
    if (shimmerTimer.current) clearTimeout(shimmerTimer.current);
    shimmerTimer.current = setTimeout(() => { shimmerTimer.current = null; setFreshKeys(new Set()); }, 2200);
  }, [keysSig]);
  useEffect(() => () => { if (shimmerTimer.current) clearTimeout(shimmerTimer.current); }, []);

  // measure each first-copy item's left offset so we can translate the strip to put
  // the active word at the far-left edge. Re-measures on layout / set changes.
  //
  // WHAT USED TO GO WRONG: this observed the RAIL, which is width:100% and so
  // never resizes when the words inside it do. On a cold load the offsets were
  // taken with fallback font metrics and then never corrected — every word in
  // the strip is a different width once the real face swaps in, so the active
  // word landed somewhere in the middle of the strip instead of at the left
  // edge, and stayed there. That is the navigator "stuck, shifted left".
  // Now we observe the STRIP (whose width tracks the words) and also wait on
  // document.fonts.ready, so the measurement survives the font swap.
  const stripRef = useRef<HTMLDivElement>(null);
  useIso(() => {
    let alive = true;
    const measure = () => {
      if (!alive) return;
      // MEASURE THE DOM, not a ref array indexed by position.
      //
      // These offsets used to come from `itemRefs.current[gi]`, filled by an
      // inline ref callback that closes over the word's index. When the sibling
      // SET grows — every space unlocking at the end of onboarding — each
      // surviving word moves to a NEW index, so React detaches its old ref
      // (writing null into the old slot) and attaches a new one. Those two
      // passes interleave across siblings, so a detach could null the very slot
      // an earlier attach had just filled. The result: a hole in `offsets`
      // exactly where the active word now lives, `activeOffset` falling back to
      // 0, and the strip left translated to 0 with the active word stranded
      // hundreds of pixels in — until some later navigation happened to
      // re-measure it. That is the navigator "shifting and only correcting when
      // you click another space".
      //
      // The strip's first `n` children ARE the first-copy words, in order. Ask
      // the DOM and there is no bookkeeping to get out of step.
      const strip = stripRef.current;
      if (!strip) return;
      const kids = Array.from(strip.children).slice(0, n) as HTMLElement[];
      if (kids.length < n) return; // mid-commit; the next measure catches it
      // same offsets, same array: a ResizeObserver tick that measured nothing new
      // must not re-render the whole navigator (MiniNav does the same)
      const next = kids.map((el) => el.offsetLeft);
      setOffsets((cur) => (cur.length === next.length && cur.every((v, i) => v === next[i]) ? cur : next));
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (stripRef.current) ro.observe(stripRef.current);
    if (railRef.current) ro.observe(railRef.current);
    // the font swap changes every word's width without resizing anything we can
    // observe reliably — re-measure once the real face has landed.
    if (typeof document !== "undefined" && "fonts" in document) {
      document.fonts.ready.then(measure).catch(() => { /* no font API — the RO stands */ });
    }
    return () => { alive = false; ro.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n, compact, keysSig, activeKey]);

  // snap (no transition) for the frame after the sibling set changes, then restore
  // the glide so ordinary section switches still ease.
  const prevSig = useRef(keysSig);
  useIso(() => {
    if (prevSig.current === keysSig) return;
    prevSig.current = keysSig;
    setSnapSet(true);
    // BOTH frames are cancelled on the way out: cancelling only the outer one let
    // a stale inner frame lift the snap a frame early when the set changed again
    let inner = 0;
    const outer = requestAnimationFrame(() => { inner = requestAnimationFrame(() => setSnapSet(false)); });
    return () => { cancelAnimationFrame(outer); cancelAnimationFrame(inner); };
  }, [keysSig]);

  const activeOffset = offsets[tickIdx] ?? 0;
  const translateX = -activeOffset;
  const setDrag = (dx: number) => {
    dragDxRef.current = dx;
    stripRef.current?.style.setProperty("--drag-dx", `${dx}px`);
  };
  // offsets are stale/short for the first frame(s) after the sibling SET changes
  // (e.g. spaces unlocking when onboarding graduates). Until they're measured for
  // the current set, suppress the glide so the strip never eases in from offset 0
  // — it just appears at the right place. Belt-and-braces with snapSet.
  const offsetsReady = offsets.length >= n && tickIdx < offsets.length;

  const step = useCallback(
    (dir: 1 | -1) => {
      if (n < 2) return;
      // from the word already pressed: two quick swipes are two steps
      const next = (tickIdx + dir + n) % n;
      go(siblings[next].key);
    },
    [tickIdx, n, siblings, go],
  );

  // THE RAIL ALWAYS ANSWERS A CLICK. A press becomes a drag only past
  // DRAG_START px of travel, and a "drag" that ends short of a section change
  // is read back as the click it almost certainly was: the pointer is captured
  // by the rail once a drag starts, so the word's own onClick never fires — a
  // trackpad click with a 5px wobble used to do nothing at all.
  const DRAG_START = 8;
  // `onSummary`: the press began on the description, not the title strip. A drag
  // that starts in a field (or the ✓ ✕ beside it), or a mouse drag on the
  // description, is a text selection and never the ticker's (railDrag.ts).
  const onRailDown = (e: React.PointerEvent, onSummary = false) => {
    if (e.button !== 0 && e.pointerType === "mouse") return;
    const target = e.target as HTMLElement | null;
    if (!mayStartRailDrag(target, e.pointerType, onSummary)) {
      dragState.current = null;
      return;
    }
    const key = target?.closest<HTMLElement>("[data-nav-key]")?.dataset.navKey ?? null;
    dragState.current = { x0: e.clientX, moved: false, pid: e.pointerId, key };
    didDragRef.current = false;
  };
  const onRailMove = (e: React.PointerEvent) => {
    const d = dragState.current;
    if (!d || e.pointerId !== d.pid) return;
    const dx = e.clientX - d.x0;
    if (!d.moved && Math.abs(dx) > DRAG_START) {
      d.moved = true;
      didDragRef.current = true;
      setDragging(true);
      try { railRef.current?.setPointerCapture(d.pid); } catch { /* ignore */ }
    }
    if (d.moved) setDrag(dx);
  };
  const onRailUp = (e?: { clientX: number; type: string }) => {
    const d = dragState.current;
    dragState.current = null;
    if (d?.moved) {
      const THRESH = 44; // px of travel to commit a section change
      const dx = e ? e.clientX - d.x0 : dragDxRef.current;
      if (dx <= -THRESH) step(1);
      else if (dx >= THRESH) step(-1);
      // short of a change and ended over the word it began on: that was a click
      else if (e && e.type === "pointerup" && Math.abs(dx) < 16 && d.key) go(d.key);
      setTimeout(() => { didDragRef.current = false; }, 0);
    }
    setDragging(false);
    // let go AFTER the glide is back on, so the strip eases home, never snaps
    requestAnimationFrame(() => setDrag(0));
  };
  // a press the browser takes back (a scroll gesture, a system sheet) must not
  // leave the rail mid-drag, holding a stale offset and eating the next click.
  const onRailCancel = () => {
    dragState.current = null;
    didDragRef.current = false;
    setDragging(false);
    requestAnimationFrame(() => setDrag(0));
  };
  // The rail's own pointer capture is best effort (the browser may refuse it, and
  // the catch above swallows that), and a drag that never hears its pointerup
  // stays `dragging` for good: the strip frozen under a grabbing cursor, no glide.
  // So while a drag is on, the WINDOW hears the release too. When the rail's own
  // handlers have already ended it there is nothing left for this to do.
  const railEnd = useRef({ up: onRailUp, cancel: onRailCancel });
  useIso(() => { railEnd.current = { up: onRailUp, cancel: onRailCancel }; });
  useEffect(() => {
    if (!dragging) return;
    const onRelease = (e: PointerEvent) => {
      const d = dragState.current;
      if (!d || e.pointerId !== d.pid) return;
      if (e.type === "pointercancel") railEnd.current.cancel();
      else railEnd.current.up(e);
    };
    const onBlur = () => { if (dragState.current) railEnd.current.cancel(); };
    window.addEventListener("pointerup", onRelease);
    window.addEventListener("pointercancel", onRelease);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("pointerup", onRelease);
      window.removeEventListener("pointercancel", onRelease);
      window.removeEventListener("blur", onBlur);
    };
  }, [dragging]);

  // One size for every sibling AND every level — active vs faded differ in
  // COLOR only, and the document (compact) view keeps the SAME title size, so
  // navigating never resizes the heading. Refined, REM-based (--os-title).
  const navSize = "var(--os-title)";
  const stat = active?.stats?.[0];
  const hasStat = !!stat || !!active?.lastActiveAt;

  // FLIP: when the description height changes between levels (or compact toggles),
  // the stat line + chips glide to their new spot instead of jumping.
  const containerRef = useRef<HTMLDivElement>(null);
  // `view` is in the deps so switching orbit↔list↔grid (same directory, only the
  // view changes — the orbit dial chip appears/disappears, chips reflow) GLIDES
  // via FLIP instead of jumping.
  // A step to another place is NOT a glide: the chips hand over with the page
  // (ChipsHandoff), so their box stands where it lands.
  useFlip(containerRef, [summary, chips.length, compact, activeKey, view], placeChanged ? { skip: ".os-chips-box" } : undefined);

  // the soft spring PRESS each sibling word gives under a finger or click. The
  // hover half of the feel is gone: framer's whileHover only clears on
  // pointerleave, which never fires when a word glides or remounts under a
  // still cursor, so it stuck. The HoverField tracker owns hover now.
  const { whileTap: pressTap, transition: pressTransition } = useHoverFeel("nudge");

  // law_scroll_lock — the folding half publishes its natural height so the
  // collapse runs off --*-fold in CSS with no per-frame JS. Measured whenever
  // the summary changes (a different level = a different number of lines).
  const foldRef = useRef<HTMLDivElement>(null);
  useNaturalHeight(foldRef, "--lock-h", `${summary}|${activeKey}|${showMatter}|${hasStat}`);
  // the reader folds the title strip itself (foldRail): it publishes its height
  const noRail = useRef<HTMLDivElement>(null);
  useNaturalHeight(foldRail ? railRef : noRail, "--lock-h", `rail|${activeKey}|${compact}`);

  return (
    <div ref={containerRef} data-os-nav style={{ display: "flex", flexDirection: "column", gap: "1.05rem" }}>
      {/* hidden scrollbar for the sibling rail — can't live in globals.css, so a
          tiny scoped rule (pseudo-elements can't be set inline). iOS momentum +
          overscroll containment are set inline below. */}
      <style>{`
        .os-sib-rail { -ms-overflow-style: none; scrollbar-width: none; }
        .os-sib-rail::-webkit-scrollbar { width: 0; height: 0; display: none; }
      `}</style>
      {/* title + siblings — a draggable, hidden-scrollbar carousel. The far-left
          item reaches fully to the edge (mask stays opaque on the left); only the
          right side fades. Drag to pan; scroll with momentum on iOS. */}
      <div
        ref={railRef}
        className={foldRail ? "os-sib-rail os-lock__fold" : "os-sib-rail"}
        data-noswipe
        // law_nav_proximity — the tracker sets data-near while the pointer is
        // within 28px (above/below) of the rail, and the words come up to meet it.
        data-hl-near="28"
        onPointerDown={onRailDown}
        onPointerMove={onRailMove}
        onPointerUp={onRailUp}
        onPointerLeave={(e) => { if (!dragState.current?.moved) onRailUp(e); }}
        onPointerCancel={onRailCancel}
        // only the RAIL losing the pointer ends a drag — a word under the finger
        // holds a capture of its own from pointerdown, and its loss (handed to
        // the rail as the drag starts) bubbles up here too
        onLostPointerCapture={(e) => { if (e.target === railRef.current && dragState.current?.moved) onRailCancel(); }}
        style={{
          overflow: "hidden",
          // vertical breathing room so big-title descenders (p/j/g/y) AND the
          // hover-nudge lift are never clipped; the fade is a long, gentle dissolve
          // only on the RIGHT so trailing siblings melt off — the left stays opaque.
          padding: "0.6rem 0",
          overscrollBehaviorX: "contain",
          WebkitMaskImage: "linear-gradient(90deg, #000 0, #000 80%, transparent 100%)",
          maskImage: "linear-gradient(90deg, #000 0, #000 80%, transparent 100%)",
          cursor: dragging ? "grabbing" : "grab",
          // a horizontal drag must own the x-axis; the page still owns vertical.
          touchAction: "pan-y",
        }}
      >
        <div
          ref={stripRef}
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: "clamp(1.4rem, 3vw, 3.2rem)",
            width: "max-content",
            paddingLeft: "0.06rem",
            transform: `translateX(calc(${translateX}px + var(--drag-dx, 0px)))`,
            // a swipe between spaces lands with grace: slower than a reply, with the
            // spring's whisper of overshoot as the active word settles at the left
            transition: dragging || snapSet || !offsetsReady ? "none" : "transform 0.5s var(--ease-glide)",
            willChange: "transform",
          }}
        >
          {/* Every sibling shows EXACTLY ONCE. When they all fit, they sit in place
              (no duplicates, no "infinite" tail). As the active word eases to the far
              left, only the words that have actually passed off the LEFT edge reappear
              — wrapped — at the right. So nothing is ever shown twice at once. */}
          {[...siblings, ...siblings.slice(0, tickIdx)].map((s, gi) => {
            const wrapped = gi >= n; // the reappear-on-the-right copy of an exited word
            const isActivePos = gi === tickIdx; // the first-copy active is the full-ink one
            const rel = gi - tickIdx; // < 0 → first copy that has slid off the left
            const blur = rel < 0 && !wrapped ? Math.min(7, -rel * 3.2) : 0;
            // ONLY the active word reads at full presence; every other sibling sits
            // clearly back (fainter ink AND lower opacity) so the active is
            // unmistakable on ANY ground — colour alone (ink vs ink-4) is too
            // subtle on a dark custom ground, where the faded words looked lit too.
            const past = rel < 0 && !wrapped; // slid off the left — keeps its blur + fade
            const op = isActivePos
              ? 1
              : past
                ? Math.max(0, 0.42 + rel * 0.16) // slid off the left — deepening fade
                : 0.46; // a trailing (or wrapped) sibling — resting, not active
            return (
              <motion.button
                key={`${s.key}-${wrapped ? "w" : "a"}`}
                // a drag that switched section must not also fire a navigation.
                onClick={() => {
                  if (didDragRef.current) return;
                  go(s.key);
                }}
                data-hint-key={s.hintKey || undefined}
                // the section this word opens — the dashboard warms that panel's
                // chunk while the pointer rests on it (law_snappy).
                data-nav-key={s.key}
                data-hint={s.hintKey ? undefined : (s.summary || undefined)}
                // law_nav_proximity — opacity + colour are CSS's (.os-sib-word):
                // --nw-op is the resting depth, data-active / data-past the role,
                // data-hl-target lets the tracker light the word under the pointer.
                className="os-sib-word"
                data-hl-target=""
                data-active={isActivePos ? "" : undefined}
                data-past={past ? "" : undefined}
                whileTap={pressTap}
                transition={pressTransition}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  letterSpacing: "-0.025em",
                  lineHeight: 1.06,
                  fontSize: navSize,
                  filter: blur ? `blur(${blur}px)` : undefined,
                  // law_snappy — the depth blur on a far word steps, it never
                  // tweens: animating a blur RADIUS re-rasterises the text every
                  // frame of the glide. Colour and opacity carry the change (in CSS).
                  ["--nw-op" as string]: op,
                  transformOrigin: "center 60%",
                } as React.CSSProperties}
              >
                {/* shimmer rides an inner span — the button's inline background:none
                    would blank a background-clip:text gradient set on itself */}
                <span className={freshKeys.has(s.key) ? "nav-word-shimmer" : undefined}>{s.name}</span>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* the DESCRIPTIVE half — the description and the stat line. When the
          navigator is pinned (law_scroll_lock) this is what folds away as you
          read, so the navigator collapses to its title strip + view chips.
          Its own flex column keeps the two at their normal spacing while they
          collapse together as one block. */}
      {!quiet && (showMatter || hasStat) && (
      <div
        ref={foldRef}
        className={fold ? "os-lock__fold" : undefined}
        style={{ display: "flex", flexDirection: "column", gap: "1.05rem", ["--lock-gap" as string]: "1.05rem" } as React.CSSProperties}
      >
        {/* description — kept even with a document open (the reader stays oriented).
            ONE size at every state (no compact shrink) so opening a document never
            jumps the type, and `balance` so the lines stay even (no long-then-short).
            Swiping the summary ALSO switches section (same ticker gesture as the strip),
            so on mobile + desktop you can flick either band to move between sections. */}
        {showMatter && (
        <div
          onPointerDown={(e) => onRailDown(e, true)}
          onPointerMove={onRailMove}
          onPointerUp={onRailUp}
          onPointerCancel={onRailCancel}
          // the description owns its own gestures: a finger flicks it (above), and a
          // mouse drag across it is a text selection. Without this the shell's
          // section swipe (useHSwipe) also read a mouse drag here and stepped the
          // section in the middle of a selection.
          data-noswipe
          data-hint-key="nav.summary"
          style={{ maxWidth: "min(46ch, 100%)", fontSize: "var(--os-desc)", lineHeight: 1.4, letterSpacing: "-0.005em", color: "var(--ink-2)", textWrap: "balance", touchAction: "pan-y" }}
        >
          {/* keyed on the section: a draft (or a half-made edit) belongs to the
              level it was begun on, and never rides to the next one — Enter after a
              section change used to write the old text into the new level's line */}
          <EditableText
            key={activeKey}
            value={summary}
            placeholder="a line about it"
            manual={summaryManual}
            onSave={onEditSummary}
            style={{ fontSize: "inherit", lineHeight: "inherit", letterSpacing: "inherit", color: "inherit", textWrap: "balance" }}
          />
        </div>
        )}

        {/* stat line — "N items"  ●  relTime  (Figma Info Status, gap 8). With the
            matter line withheld, the generated description rides here as a hover hint. */}
        {hasStat && (
        <div
          data-hint={!showMatter && summary ? summary : undefined}
          style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "var(--t-sm)" }}
        >
          {stat && <span style={{ color: "var(--ink-3)" }}>{stat.value} {stat.label}</span>}
          {active?.lastActiveAt && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>
              <span className="ink-dot" />
              <span style={{ color: "var(--ink-2)" }}>{relTime(active.lastActiveAt)}</span>
            </span>
          )}
        </div>
        )}
      </div>
      )}

      {/* view chips (gap 16) — only when this level actually has views to show;
          the "+ new view" affordance rides along, so it's hidden too when empty.
          Tap to select; press-and-hold to rearrange (leftmost = the default). */}
      {/* the place's chips: a folder's, drawn here, or a panel's, handed into
          the slot (NavChips). The slot always stands, so a panel's row goes
          straight into it on its first render instead of being drawn in the
          content and moved up a render later. Both hand over with the page. */}
      {(chips.length > 0 || chipSlot) && (
      <ChipsHandoff placeKey={activeKey} dir={placeDir.current}>
      {chips.length > 0 && (
        <ReorderableChips
          key={activeKey}
          chips={chips}
          view={view}
          onView={onView}
          onNewView={onNewView}
          onReorder={onReorderChips}
          allowNew={allowNewView}
          renderAccessory={renderChipAccessory}
          unseen={unseenViews}
          trailing={chipTrailing}
          onChipContextMenu={onChipContextMenu}
        />
      )}
      {chipSlot && <div ref={chipSlot} className="os-chip-slot" />}
      </ChipsHandoff>
      )}
    </div>
  );
}
